import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { FormGenerator } from '@microservices/wiskey-react-components'
import { Box, Typography } from '@mui/material'

import { ObjectXLinkObjectDataTab } from '@pages/TimeLineCreateOrEdit/components/ObjectXConfiguration/components/ObjectXLinkObjectDataTab'
import { getObjectXCellScriptHint } from '@pages/TimeLineCreateOrEdit/helpers'

import { ConfigRestrictions } from '@components/ConfigRestrictions/ConfigRestrictions'
import { JavaScriptField } from '@components/JavaScriptField'
import { SwitchTable } from '@components/SwitchTable'
import { Tab, TabPanel, Tabs } from '@components/Tabs'

import {
  BIND_TYPE,
  MODEL_TYPE,
  TIME_LINE_MODE,
  TIMELINE_ACTION_COLUMNS,
  TIMELINE_OBJECT_X_COLUMNS,
} from '@constants'

import { getIsErrorTab } from '@gantt/helpers'

import { PageContext } from '../../TimeLineCreateOrEdit'
import { ObjectXTotals } from '../ObjectXTotalsTab'

import { CellPreview } from './components/CellPreview'
import { CellSizeSettings } from './components/CellSizeSettings'
import { ObjectXDateTimeDataTab } from './components/ObjectXDateTimeDataTab/ObjectXDateTimeDataTab'

export const ObjectXConfiguration = () => {
  const { state, handlers, inputs, data } = useContext(PageContext)
  const { t } = useTranslation()

  const { objectYSelectedObjectFields, objectXSelectedObjectFields } = data

  const {
    watchObjectXColumns,
    objectXShowAddColumnDialog,
    objectXCurrentTab,
    objectXShowCellScriptValueDialog,
    watchObjectXCellBindType,
    watchObjectXCellValue,
    watchObjectXActions,
    objectXShowAddActionDialog,
    watchVariables,
    watchId,
    watchObjectXEntityId,
    watchMode,
    methods: {
      formState: { errors },
      watch,
      setValue,
    },
    objectXCellPreviewData,
  } = state

  const watchObjectXCellMinWidth = watch('objectX.cell.minWidth')
  const watchObjectXCellMinHeight = watch('objectX.cell.minHeight')

  const {
    handleOpenObjectXAddColumnDialog,
    handleChangeObjectXCurrentTab,
    handleChangeObjectXShowCellScriptValueDialog,
    handleChangeObjectXCellValue,
    handleDeleteObjectXColumn,
    handleOpenObjectXAddActionDialog,
    handleDeleteObjectXAction,
  } = handlers

  const { objectXCellInputs } = inputs

  const handleMinWidthChange = (value: number) => {
    setValue('objectX.cell.minWidth', value, { shouldDirty: true })
  }

  const handleMinHeightChange = (value: number) => {
    setValue('objectX.cell.minHeight', value, { shouldDirty: true })
  }

  const handleApplyToConfig = (width: number, height: number) => {
    handleMinWidthChange(width)
    handleMinHeightChange(height)
  }

  const isShowRestrictionsTab =
    Boolean(watchObjectXEntityId) && Boolean(watchObjectXColumns?.length)

  const cellValueError = errors.objectX?.cell?.value
  const objectXCellScriptErrorText = cellValueError?.message?.trim()

  const renderDataTab = () => {
    if (watchMode?.id === TIME_LINE_MODE.DATE_TIME) {
      return <ObjectXDateTimeDataTab />
    }

    if (watchMode?.id === TIME_LINE_MODE.OBJECT) {
      return <ObjectXLinkObjectDataTab />
    }

    return null
  }

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={objectXCurrentTab} onChange={handleChangeObjectXCurrentTab}>
          <Tab
            tabIndex={0}
            label={
              <Typography
                variant={'body2'}
                color={getIsErrorTab(errors, [
                  'objectX.object',
                  'objectX.linkToObjectY',
                  'objectX.command',
                  'objectX.keyField',
                  'objectX.columnsObject',
                ])}
              >
                {t(`timeLinesCreateOrEdit.objectX.tabs.data`)}
              </Typography>
            }
          />
          <Tab
            tabIndex={1}
            label={
              <Typography
                variant={'body2'}
                color={getIsErrorTab(errors, [
                  'objectX.cell.value',
                  'objectX.cell.bindType',
                  'objectX.cell.entity',
                ])}
              >
                {t(`timeLinesCreateOrEdit.objectX.tabs.cell`)}
              </Typography>
            }
          />
          <Tab label={t(`timeLinesCreateOrEdit.objectX.tabs.columns`)} tabIndex={2} />
          {isShowRestrictionsTab && (
            <Tab label={t(`timeLinesCreateOrEdit.objectY.tabs.restrictions`)} tabIndex={3} />
          )}
          <Tab
            label={t(`timeLinesCreateOrEdit.objectY.tabs.actions`)}
            tabIndex={3 + Number(isShowRestrictionsTab)}
          />
          <Tab
            label={t(`timeLinesCreateOrEdit.objectX.tabs.totals`)}
            tabIndex={4 + Number(isShowRestrictionsTab)}
          />
        </Tabs>
      </Box>
      <TabPanel index={0} sx={{ p: 1 }} value={objectXCurrentTab}>
        {renderDataTab()}
      </TabPanel>
      <TabPanel index={1} sx={{ p: 1 }} value={objectXCurrentTab}>
        <FormGenerator inputs={objectXCellInputs} mainGridSpacing={0} />
        <JavaScriptField
          errorText={objectXCellScriptErrorText}
          isShowScriptDialog={objectXShowCellScriptValueDialog}
          language={watchObjectXCellBindType?.id === BIND_TYPE.JSX ? BIND_TYPE.JSX : BIND_TYPE.JS}
          scriptValue={watchObjectXCellValue}
          onCloseScriptDialog={() => handleChangeObjectXShowCellScriptValueDialog(false)}
          onOpenScriptDialog={() => handleChangeObjectXShowCellScriptValueDialog(true)}
          onSaveScript={handleChangeObjectXCellValue}
          {...getObjectXCellScriptHint({
            variables: watchVariables,
            objectXFields: objectXSelectedObjectFields,
            objectYFields: objectYSelectedObjectFields,
          })}
        />
        <Box sx={{ mt: 2 }}>
          <CellSizeSettings
            minHeight={watchObjectXCellMinHeight}
            minWidth={watchObjectXCellMinWidth}
            onMinHeightChange={handleMinHeightChange}
            onMinWidthChange={handleMinWidthChange}
          />
          <CellPreview
            bindType={watchObjectXCellBindType?.id as string}
            previewData={objectXCellPreviewData}
            value={watchObjectXCellValue}
            onApplyToConfig={handleApplyToConfig}
          />
        </Box>
      </TabPanel>
      <TabPanel index={2} sx={{ p: 1 }} value={objectXCurrentTab}>
        <SwitchTable
          disablePagination
          showActionsColumn
          btnDisabled={!watchId}
          btnText={t(`timeLinesCreateOrEdit.addColumn`)}
          columns={TIMELINE_OBJECT_X_COLUMNS}
          rows={watchObjectXColumns}
          showDialog={objectXShowAddColumnDialog}
          type={MODEL_TYPE.TIMELINE_OBJECT_X_COLUMN}
          onDelete={handleDeleteObjectXColumn}
          onShowDialog={handleOpenObjectXAddColumnDialog}
          onEdit={params => {
            handleOpenObjectXAddColumnDialog('edit', params.row.original.id)
          }}
        />
      </TabPanel>
      {isShowRestrictionsTab && (
        <TabPanel index={3} value={objectXCurrentTab}>
          <ConfigRestrictions
            hiddenColumns={['sort', 'columnToSort', 'visibility', 'qs']}
            rows={watchObjectXColumns}
            viewId={watchObjectXEntityId as number}
          />
        </TabPanel>
      )}
      <TabPanel index={isShowRestrictionsTab ? 4 : 3} sx={{ p: 1 }} value={objectXCurrentTab}>
        <SwitchTable
          disablePagination
          showActionsColumn
          btnDisabled={!watchId}
          btnText={t(`timeLinesCreateOrEdit.addAction`)}
          columns={TIMELINE_ACTION_COLUMNS}
          rows={watchObjectXActions}
          showDialog={objectXShowAddActionDialog}
          type={MODEL_TYPE.TIMELINE_OBJECT_X_ACTION}
          onDelete={handleDeleteObjectXAction}
          onShowDialog={handleOpenObjectXAddActionDialog}
          onEdit={params => {
            handleOpenObjectXAddActionDialog('edit', params.row.original.id)
          }}
        />
      </TabPanel>
      <TabPanel index={isShowRestrictionsTab ? 5 : 4} sx={{ p: 1 }} value={objectXCurrentTab}>
        <ObjectXTotals />
      </TabPanel>
    </>
  )
}
